// iOS-Safari-PWA-Fix: der Layout-Viewport meldet beim Cold-Start eine
// falsche Hoehe, "fixed; bottom: 0"-Elemente landen dadurch zu hoch
// und ruecken erst nach dem ersten Touch/Scroll an die richtige Stelle.
// Das ist ein bekannter WebKit-Bug; saubere Loesung ist, sich auf die
// VisualViewport-API zu stuetzen und einen pixelgenauen Bottom-Offset
// per CSS-Variable zu pflegen.
//
// Wir setzen --app-vh = visualViewport.height (in px). Die MobileTabBar
// dockt mit `top` an `calc(var(--app-vh) - tab-height)` an, statt mit
// `bottom: 0` -- so folgt sie der wirklich-sichtbaren Hoehe.
//
// Tim's Bug 2026-05-13: TabBar steht beim Re-Open zu hoch.
// Nachtrag 2026-07-29: trat weiter sporadisch auf, weil JEDER
// visualViewport-Zwischenwert uebernommen wurde. Siehe ansicht().
//
// Tastatur (Tim 22.10.2026: „Das Menü ganz unten bleibt nicht unten. Will
// man etwas eintippen, verschwindet die Textbox aus dem Sichtbereich" --
// „Das macht Apple schlecht. Dafür brauchen wir mal eine gescheite
// Lösung"): iOS verkleinert beim Aufgehen der Tastatur NICHT den Layout-
// Viewport, sondern schiebt den sichtbaren Ausschnitt darüber
// (visualViewport.offsetTop) -- die ganze Seite rutscht nach oben. Bis
// dahin folgte die App-Hülle nur der HÖHE; die Leiste unten ritt auf der
// Tastatur, das Eingabefeld rutschte aus dem Bild. Jetzt folgt die Hülle
// dem sichtbaren Ausschnitt ganz (--app-vv-top + --app-vh, App.svelte),
// <html data-tastatur> blendet die untere Leiste aus (wie native Apps:
// sie liegt hinter der Tastatur), das fokussierte Feld wird in seinem
// Rollbereich sichtbar gehalten, und nach dem Zuklappen setzt die App die
// Verschiebung zurück, die iOS gern stehen lässt.

const VAR_HEIGHT = "--app-vh";
const VAR_BOTTOM = "--app-vv-bottom";
const VAR_TOP = "--app-vv-top";
const ATTR_TASTATUR = "data-tastatur";

// Schwelle, ab der eine kleinere Visual-Viewport-Hoehe wirklich als
// Tastatur gilt. Alles darunter sind Zwischenstaende, die iOS beim
// Scroll-Bounce, beim Cold-Start und beim Zurueckkehren aus dem
// Hintergrund kurz meldet.
export const KEYBOARD_MIN = 120;

export interface Ansicht {
  /** Höhe der App-Hülle in px */
  hoehe: number;
  /** Abstand des sichtbaren Ausschnitts vom oberen Rand des Layouts */
  oben: number;
  /** von der Tastatur verdeckter Teil unten */
  unten: number;
  tastatur: boolean;
}

/** Rechnet aus Layout-Höhe und sichtbarem Ausschnitt, wo die App-Hülle
 *  stehen soll. Der Layout-Viewport (innerHeight) ist in einer Standalone-
 *  PWA die STABILE Quelle; der sichtbare Ausschnitt zählt nur, wenn
 *  wirklich etwas ihn verkleinert -- praktisch die Tastatur. Vorher wurde
 *  jeder Zwischenwert übernommen: ein Scroll-Bounce oder ein Cold-Start-
 *  Frame schrieb eine zu kleine Höhe fest (Tim 2026-07-29). */
export function ansicht(inner: number, vv: { height: number; offsetTop: number } | null): Ansicht {
  const geschrumpft = vv ? inner - vv.height : 0;
  if (!vv || geschrumpft <= KEYBOARD_MIN) {
    return { hoehe: inner, oben: 0, unten: 0, tastatur: false };
  }
  const oben = Math.max(0, vv.offsetTop);
  return { hoehe: vv.height, oben, unten: Math.max(0, inner - vv.height - oben), tastatur: true };
}

/** Das fokussierte Element, wenn man darin tippen kann. */
function eingabeImFokus(): HTMLElement | null {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return null;
  if (el.isContentEditable) return el;
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return el;
  if (el instanceof HTMLInputElement) {
    return ["checkbox", "radio", "range", "button", "submit", "reset", "file", "color"].includes(el.type) ? null : el;
  }
  return null;
}

let warTastatur = false;
let sichtbarHalten = 0;

function setze(root: HTMLElement, name: string, wert: string): void {
  // Nur schreiben, wenn sich etwas ändert -- sonst löst jeder Scroll-Tick
  // ein Re-Layout der ganzen Shell aus.
  if (root.style.getPropertyValue(name) !== wert) root.style.setProperty(name, wert);
}

function syncViewport(): void {
  const vv = window.visualViewport;
  const a = ansicht(window.innerHeight, vv ? { height: vv.height, offsetTop: vv.offsetTop } : null);
  const root = document.documentElement;
  setze(root, VAR_HEIGHT, `${Math.round(a.hoehe)}px`);
  setze(root, VAR_TOP, `${Math.round(a.oben)}px`);
  setze(root, VAR_BOTTOM, `${Math.round(a.unten)}px`);

  if (a.tastatur !== warTastatur) {
    warTastatur = a.tastatur;
    if (a.tastatur) root.setAttribute(ATTR_TASTATUR, "");
    else {
      root.removeAttribute(ATTR_TASTATUR);
      // iOS lässt nach dem Zuklappen oft eine Verschiebung stehen: die
      // Seite stand dann ein Stück zu hoch, die Leiste nicht unten.
      if (window.scrollY !== 0 || window.scrollX !== 0) window.scrollTo(0, 0);
    }
  }
  if (a.tastatur) feldSichtbarHalten();
}

/** Nach dem Umbau der Hülle liegt das Feld vielleicht außerhalb seines
 *  Rollbereichs (iOS hat für die alte, größere Hülle gerechnet): einmal
 *  je Bild nachziehen. */
function feldSichtbarHalten(): void {
  if (sichtbarHalten) return;
  sichtbarHalten = requestAnimationFrame(() => {
    sichtbarHalten = 0;
    const el = eingabeImFokus();
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vv = window.visualViewport;
    const unten = vv ? vv.height : window.innerHeight;
    // Koordinaten relativ zum sichtbaren Ausschnitt: die Hülle steht
    // seit dem Umbau bündig darin.
    if (r.top < 0 || r.bottom > unten) el.scrollIntoView({ block: "nearest", inline: "nearest" });
  });
}

export function startViewportSync(): void {
  // Erst-Sync sofort. Dann mehrfach in den ersten paar Frames, weil iOS-
  // Safari-PWA die korrekten Werte erst nach dem ersten Layout-Pass
  // ausspuckt.
  syncViewport();
  requestAnimationFrame(syncViewport);
  setTimeout(syncViewport, 100);
  setTimeout(syncViewport, 500);
  setTimeout(syncViewport, 1500);

  // iOS-Standalone-PWA-Workaround: ein winziger Scroll-Wackler zwingt
  // WebKit, den Layout-Viewport gegen den echten Bildschirm neu
  // aufzurechnen. Klingt nach Hack -- ist aber der dokumentierte Trick
  // gegen den "Initial Frame zu hoch"-Bug ohne Touch-Input.
  setTimeout(() => {
    window.scrollTo(0, 1);
    window.scrollTo(0, 0);
    syncViewport();
  }, 50);

  // Listener: alle Viewport-Aenderungen sofort uebernehmen.
  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener("resize", syncViewport);
    vv.addEventListener("scroll", syncViewport);
  }
  window.addEventListener("resize", syncViewport);
  window.addEventListener("orientationchange", syncViewport);
  // Fokuswechsel ohne Größenänderung (von einem Feld ins nächste): das
  // neue Feld ebenfalls sichtbar halten.
  document.addEventListener("focusin", () => {
    if (warTastatur) feldSichtbarHalten();
  });
  // Wenn die PWA aus dem Background zurueckkommt (App-Switcher,
  // Bildschirm-Lock), das Viewport nochmal frisch lesen.
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) syncViewport();
  });
  window.addEventListener("pageshow", syncViewport);
}
